"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { getAccessToken } from "./auth-storage";
import { getCurrentUserService } from "@/domains/users/user.service";

// Rotas públicas que não requerem autenticação
const PUBLIC_ROUTES = [
  "/",
  "/about",
  "/privacy",
  "/terms",
  "/changelog",
  "/request-presentation",
];

// Rotas de autenticação que devem redirecionar se já autenticado
const AUTH_ROUTES = [
  "/sign-in",
  "/sign-up",
  "/reset-password",
];

// Rotas especiais que permitem acesso mesmo sem token
const SPECIAL_ROUTES = [
  "/sign-out",
];

/**
 * Componente que gerencia a autenticação client-side
 * - Redireciona para /dashboard se já autenticado e tenta acessar rotas de auth
 * - Protege rotas privadas redirecionando para /sign-in
 * - Pré-carrega dados do usuário em rotas autenticadas
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();

  useEffect(() => {
    const accessToken = getAccessToken();
    const isPublicRoute = PUBLIC_ROUTES.some((route) => pathname === route || pathname.startsWith(route));
    const isAuthRoute = AUTH_ROUTES.some((route) => pathname === route || pathname.startsWith(route));
    const isSpecialRoute = SPECIAL_ROUTES.some((route) => pathname === route || pathname.startsWith(route));

    // Se tem token válido e está tentando acessar rotas de autenticação (sign-in, sign-up)
    // redireciona para o dashboard
    if (accessToken && isAuthRoute) {
      console.log("Usuário já autenticado, redirecionando para dashboard...");
      router.push("/dashboard");
      return;
    }

    // Se não tem token e está tentando acessar rota privada, redireciona para login
    if (!accessToken && !isPublicRoute && !isAuthRoute && !isSpecialRoute) {
      router.push("/sign-in");
      return;
    }

    // Se tem token e está em uma rota autenticada (privada), pré-carrega dados do usuário
    if (accessToken && !isPublicRoute && !isAuthRoute && !isSpecialRoute) {
      queryClient.prefetchQuery({
        queryKey: ["user", "me"],
        queryFn: () => getCurrentUserService(),
      });
    }
  }, [pathname, router, queryClient]);

  return <>{children}</>;
}
